import { useCallback } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';

/**
 * Custom hook for URL-based view navigation in TabView3 components
 * 
 * @param {Object} options Configuration options
 * @param {boolean} options.enabled Whether URL view navigation is enabled (default: true)
 * @param {string} options.paramName URL parameter name for the view (default: 'view')
 * 
 * @returns {Object} Object containing URL navigation functions and state
 */
export const useTabViewUrl = (options = {}) => {
    const { enabled = true, paramName = 'view' } = options;
    const location = useLocation();
    const navigate = useNavigate();

    /**
     * Get the current view from URL parameters
     * @returns {string|null} Current view name or null if not found
     */
    const getCurrentViewFromUrl = useCallback(() => {
        if (!enabled) return null;
        
        const searchParams = new URLSearchParams(location.search);
        return searchParams.get(paramName);
    }, [enabled, paramName, location.search]);

    /**
     * Update the URL with a new view name
     * @param {string} viewName Name of the view to set in URL
     * @param {boolean} replace Whether to replace history entry (default: true)
     */
    const setViewInUrl = useCallback((viewName, replace = true) => {
        if (!enabled || !viewName) return;
        
        const searchParams = new URLSearchParams(location.search);
        searchParams.set(paramName, viewName);
        
        const newUrl = `${location.pathname}?${searchParams.toString()}`;
        navigate(newUrl, { replace });
    }, [enabled, paramName, location.pathname, location.search, navigate]);

    /**
     * Remove view parameter from URL
     * @param {boolean} replace Whether to replace history entry (default: true)
     */
    const clearViewFromUrl = useCallback((replace = true) => {
        if (!enabled) return;
        
        const searchParams = new URLSearchParams(location.search);
        searchParams.delete(paramName);
        
        const queryString = searchParams.toString();
        const newUrl = queryString ? `${location.pathname}?${queryString}` : location.pathname;
        navigate(newUrl, { replace });
    }, [enabled, paramName, location.pathname, location.search, navigate]);

    /**
     * Check if a specific view is currently active in the URL
     * @param {string} viewName Name of the view to check
     * @returns {boolean} True if the view is currently active
     */
    const isViewActive = useCallback((viewName) => {
        if (!enabled || !viewName) return false;
        return getCurrentViewFromUrl() === viewName;
    }, [enabled, getCurrentViewFromUrl]);

    return {
        enabled,
        getCurrentViewFromUrl,
        setViewInUrl,
        clearViewFromUrl,
        isViewActive,
        currentView: getCurrentViewFromUrl()
    };
};

export default useTabViewUrl; 